import type { Department } from '@/lib/departments'
import { DEPARTMENT_BY_VALUE } from '@/lib/departments'

export type InboxUrgency = 'low' | 'medium' | 'high' | 'urgent'

export type InboxPrimaryType =
  | 'public_concert'
  | 'casino_pac_theatre'
  | 'festival'
  | 'corporate_gala'
  | 'private_event'
  | 'international'
  | 'residency'
  | 'destination'
  | 'legal'
  | 'accounts'
  | 'licensing'
  | 'broker_application'
  | 'press'
  | 'unknown'

export interface ParsedExtractedFields {
  contactName?: string
  contactOrg?: string
  eventDate?: string
  eventCity?: string
  eventCountry?: string
  venueName?: string
  venueCapacity?: number
  budgetMin?: number
  budgetMax?: number
}

export interface ParsedClassification {
  primaryType: InboxPrimaryType
  primaryLabel: string
  secondaryTypes: string[]
  confidence: number | null
  summary: string | null
  urgency: InboxUrgency | null
  urgencyLabel: string | null
  extractedFields: ParsedExtractedFields
  routingGroups: string[]
}

const PRIMARY_TYPE_LABELS: Record<InboxPrimaryType, string> = {
  public_concert: 'Public concert',
  casino_pac_theatre: 'Casino / PAC / Theatre',
  festival: 'Festival',
  corporate_gala: 'Corporate / Gala',
  private_event: 'Private event',
  international: 'International',
  residency: 'Residency',
  destination: 'Destination',
  legal: 'Legal',
  accounts: 'Accounts / Finance',
  licensing: 'Licensing',
  broker_application: 'Broker application',
  press: 'Press / Media',
  unknown: 'Unclassified',
}

const URGENCY_LABELS: Record<InboxUrgency, string> = {
  urgent: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined
}

function asNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const n = Number(value.replace(/,/g, ''))
    return Number.isFinite(n) ? n : undefined
  }
  return undefined
}

function asPrimaryType(value: unknown): InboxPrimaryType {
  if (typeof value === 'string' && value in PRIMARY_TYPE_LABELS) {
    return value as InboxPrimaryType
  }
  return 'unknown'
}

function asUrgency(value: unknown): InboxUrgency | null {
  if (value === 'low' || value === 'medium' || value === 'high' || value === 'urgent') {
    return value
  }
  return null
}

/** Safely parse stored classification JSON from the API. */
export function parseClassification(raw: unknown): ParsedClassification | null {
  if (!raw || typeof raw !== 'object') return null
  const c = raw as Record<string, unknown>
  const primaryType = asPrimaryType(c.primaryType)
  const extracted = (c.extractedFields && typeof c.extractedFields === 'object'
    ? c.extractedFields
    : {}) as Record<string, unknown>
  const urgency = asUrgency(c.urgency)

  const extractedFields: ParsedExtractedFields = {}
  const contactName = asString(extracted.contactName)
  const contactOrg = asString(extracted.contactOrg)
  const eventDate = asString(extracted.eventDate)
  const eventCity = asString(extracted.eventCity)
  const eventCountry = asString(extracted.eventCountry)
  const venueName = asString(extracted.venueName)
  const venueCapacity = asNumber(extracted.venueCapacity)
  const budgetMin = asNumber(extracted.budgetMin)
  const budgetMax = asNumber(extracted.budgetMax)
  if (contactName) extractedFields.contactName = contactName
  if (contactOrg) extractedFields.contactOrg = contactOrg
  if (eventDate) extractedFields.eventDate = eventDate
  if (eventCity) extractedFields.eventCity = eventCity
  if (eventCountry) extractedFields.eventCountry = eventCountry
  if (venueName) extractedFields.venueName = venueName
  if (venueCapacity != null) extractedFields.venueCapacity = venueCapacity
  if (budgetMin != null) extractedFields.budgetMin = budgetMin
  if (budgetMax != null) extractedFields.budgetMax = budgetMax

  return {
    primaryType,
    primaryLabel: PRIMARY_TYPE_LABELS[primaryType],
    secondaryTypes: Array.isArray(c.secondaryTypes)
      ? c.secondaryTypes.filter((s): s is string => typeof s === 'string')
      : [],
    confidence: asNumber(c.confidence) ?? null,
    summary: asString(c.summary) ?? null,
    urgency,
    urgencyLabel: urgency ? URGENCY_LABELS[urgency] : null,
    extractedFields,
    routingGroups: Array.isArray(c.routingGroups)
      ? c.routingGroups.filter((s): s is string => typeof s === 'string')
      : [],
  }
}

export function formatConfidence(confidence: number | null): string | null {
  if (confidence == null) return null
  return `${Math.round(confidence * 100)}%`
}

export function formatBudgetRange(min?: number, max?: number): string | null {
  if (min == null && max == null) return null
  const fmt = (n: number) =>
    new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: 'GBP',
      maximumFractionDigits: 0,
    }).format(n)
  if (min != null && max != null) return `${fmt(min)} – ${fmt(max)}`
  if (min != null) return `From ${fmt(min)}`
  return `Up to ${fmt(max!)}`
}

export function urgencyBadgeClass(urgency: InboxUrgency | null): string {
  switch (urgency) {
    case 'urgent':
      return 'bg-red-100 text-red-800'
    case 'high':
      return 'bg-orange-100 text-orange-800'
    case 'medium':
      return 'bg-amber-100 text-amber-800'
    case 'low':
      return 'bg-slate-100 text-slate-600'
    default:
      return 'bg-slate-100 text-slate-600'
  }
}

export interface RoutingSummary {
  ruleName: string | null
  action: string | null
  actionLabel: string
  forwardedTo: string | null
  targetDepartment: Department | null
  departmentLabel: string | null
  layer: string | null
  appliedAt: string | null
  manual: boolean
  inquiryCreated: boolean
}

const ACTION_LABELS: Record<string, string> = {
  create_inquiry: 'Created inquiry',
  forward_to_role: 'Forwarded to team',
  forward_to_email: 'Forwarded to email',
  auto_reply: 'Auto-replied',
  archive: 'Archived',
  mark_spam: 'Marked spam',
  automated_transactional: 'Ingested to finance',
  manual_forward: 'Forwarded manually',
  manual_archive: 'Removed from inbox',
}

export function parseRoutingDecision(
  raw: unknown,
  inquiryId?: string | null,
): RoutingSummary | null {
  if (!raw || typeof raw !== 'object') return null
  const d = raw as Record<string, unknown>
  const action = typeof d.action === 'string' ? d.action : null
  const fwd = d.forwardedTo
  let forwardedTo: string | null = null
  if (typeof fwd === 'string') forwardedTo = fwd
  else if (Array.isArray(fwd)) forwardedTo = fwd.filter((x) => typeof x === 'string').join(', ')

  const dept =
    typeof d.targetDepartment === 'string' ? (d.targetDepartment as Department) : null

  return {
    ruleName: typeof d.ruleName === 'string' ? d.ruleName : null,
    action,
    actionLabel: action
      ? (ACTION_LABELS[action] ?? action.replace(/_/g, ' '))
      : 'No routing action',
    forwardedTo,
    targetDepartment: dept,
    departmentLabel: dept ? (DEPARTMENT_BY_VALUE[dept]?.label ?? dept) : null,
    layer: typeof d.layer === 'string' ? d.layer : null,
    appliedAt: typeof d.appliedAt === 'string' ? d.appliedAt : null,
    manual: d.manual === true,
    inquiryCreated: Boolean(inquiryId) || action === 'create_inquiry',
  }
}

export function buildRecommendedActions(input: {
  classification: ParsedClassification | null
  department: Department | null | undefined
  assignedName: string | null
}): string[] {
  const actions: string[] = []
  const { classification: c, department, assignedName } = input

  if (assignedName) actions.push(`Notify ${assignedName}`)
  else if (department) {
    const label = DEPARTMENT_BY_VALUE[department]?.label ?? department
    actions.push(`Notify ${label}`)
  }

  if (!c) return actions.length ? actions : ['Review message']

  if (c.urgency === 'urgent' || c.urgency === 'high') {
    actions.push('Respond today')
  }

  switch (c.primaryType) {
    case 'legal':
      actions.push('Review legal matter', 'Create legal task')
      break
    case 'accounts':
      actions.push('Review invoice or payment', 'Forward to accounts')
      break
    case 'press':
      actions.push('Review press opportunity', 'Check media calendar')
      break
    case 'licensing':
      actions.push('Review sync/licensing terms')
      break
    case 'broker_application':
      actions.push('Review broker credentials')
      break
    default:
      if (
        c.primaryType !== 'unknown' &&
        !['legal', 'accounts', 'press', 'licensing'].includes(c.primaryType)
      ) {
        actions.push('Review booking details')
        if (c.extractedFields.eventDate) actions.push('Check calendar availability')
      }
      break
  }

  if (c.extractedFields.contactOrg) actions.push('Update CRM contact')
  actions.push('Draft initial response')

  return [...new Set(actions)]
}

export function classificationListPreview(raw: unknown): {
  category: string | null
  urgency: InboxUrgency | null
} {
  const c = parseClassification(raw)
  if (!c) return { category: null, urgency: null }
  const secondary =
    c.secondaryTypes.length > 0 ? ` → ${c.secondaryTypes[0]}` : ''
  return {
    category: `${c.primaryLabel}${secondary}`,
    urgency: c.urgency,
  }
}
